/**
 * Scene protocol — the shared language between workspaces (Phase A1).
 *
 * Every object that lives in the document (BIM element, CAD entity,
 * NURBS shape) speaks this protocol so that anything drawn at one
 * workbench is visible, selectable and manipulable at every other.
 *
 * HARD RULES (see docs/architecture/single-app-integration-plan.md):
 * - Mod-specific types (BIMElement, Entity, ...) are NOT merged. They keep
 *   their rich models and are PROJECTED into SceneObject via adapters.
 * - No mode branching (`if source === 'bim'`) is allowed in core consumers;
 *   special behaviour is attached from the outside as providers.
 * - `placement` is always expressed in base units (meters), normalized
 *   through the shared coordinate system.
 */

/** Which workbench created / owns the living copy of the object. */
export type SceneSource = 'bim' | 'cad' | 'nurbs' | 'sketch';

/**
 * Normalized semantic vocabulary shared by all workbenches.
 * Adapters map mod-specific categories/types onto these kinds;
 * cross-mode representation rules (line weights, plan-cut styles, ...)
 * are keyed off `kind`, never off raw category strings.
 */
export type SceneKind =
    // --- BIM categories ---
    | 'wall'
    | 'door'
    | 'window'
    | 'floor'
    | 'roof'
    | 'ceiling'
    | 'beam'
    | 'column'
    | 'truss'
    | 'brace'
    | 'stair'
    | 'railing'
    | 'curtainWall'
    | 'mass'
    | 'room'
    | 'duct'
    | 'pipe'
    | 'cableTray'
    | 'conduit'
    | 'equipment'
    | 'fixture'
    | 'structural'
    | 'annotation'
    | 'detail'
    | 'link'
    | 'group'
    | 'decal'
    | 'level'
    | 'grid'
    // --- CAD drafting ---
    | 'line'
    | 'polyline'
    | 'circle'
    | 'arc'
    | 'ellipse'
    | 'text'
    | 'dimension'
    | 'hatch'
    | 'block'
    | 'point'
    | 'mesh'
    | 'image'
    | 'pointcloud'
    // --- NURBS (reserved, citizens arrive with Phase B3) ---
    | 'curve'
    | 'surface'
    | 'subd'
    // --- escape hatch for anything not yet in the vocabulary ---
    | 'generic';

/** Axis-angle-free, serialization-friendly placement triplets (base units). */
export interface Placement {
    position: [number, number, number];
    /** Euler radians, XYZ order (matches THREE.Object3D defaults). */
    rotation: [number, number, number];
    scale: [number, number, number];
}

/** Optional world-space AABB; produced by geometry providers, not by adapters. */
export interface AABB {
    min: [number, number, number];
    max: [number, number, number];
}

/**
 * The protocol every scene citizen conforms to.
 *
 * `data` is the mod-specific serializable payload: it is opaque to shared
 * code, and only the owning mod's store/renderer knows how to read it.
 * Shared code MUST NOT invent new required fields here; add a field to
 * this interface (or a typed payload interface) when the contract grows.
 */
export interface SceneObject {
    /** Unique id from the single id space (see id.ts). Never reused. */
    id: string;
    source: SceneSource;
    /** Normalized semantic kind (shared representation vocabulary). */
    kind: SceneKind;
    /** Human-readable label shown in any workbench (names, tags, types). */
    displayName: string;
    /**
     * Shared visibility contract id. CAD entities report their layer name;
     * BIM elements report their category. The VisibilityResolver (Phase A4)
     * is the single consumer that turns this into shown/hidden.
     */
    layerId: string;
    visible: boolean;
    locked: boolean;
    placement: Placement;
    /** Opaque per-source payload owned by the creating workbench. */
    data: Record<string, unknown>;
    /** Cached world bounds if the producing side already knows them. */
    bounds?: AABB;
    /** Optional provenance for linked/imported content (LinkManager ids). */
    linkId?: string;
}

/** Default identity placement — reused via factory to avoid allocation drift. */
export function identityPlacement(): Placement {
    return { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] };
}

/** Type guard for anything claiming to speak the protocol. */
export function isSceneObject(value: unknown): value is SceneObject {
    if (typeof value !== 'object' || value === null) return false;
    const v = value as Partial<SceneObject>;
    return (
        typeof v.id === 'string' &&
        (v.source === 'bim' || v.source === 'cad' || v.source === 'nurbs' || v.source === 'sketch') &&
        typeof v.kind === 'string' &&
        typeof v.layerId === 'string' &&
        typeof v.visible === 'boolean' &&
        typeof v.locked === 'boolean' &&
        Array.isArray(v.placement?.position) &&
        Array.isArray(v.placement?.rotation) &&
        Array.isArray(v.placement?.scale) &&
        typeof v.data === 'object' &&
        v.data !== null
    );
}
